import React, { FC, useEffect, useState, useCallback } from 'react'
import { Button, Modal, Spinner, Badge, OverlayTrigger, Tooltip, Alert } from 'react-bootstrap'
import { useAppDispatch, useAppSelector } from 'src/redux/storeHooks'
import useAppToast, { getApiErrorMsg } from 'src/hooks/useAppToast'
import {
  getOmsShipmentListAsync,
  deleteOmsShipmentAsync,
} from 'src/redux/actions/omsShippingActions'
import { getOrderLinesForPackageAsync } from 'src/redux/actions/orderPackageActions'
import { determineShippingRoute, ShippingRoute, hasPendingQty } from './determineShippingRoute'
import ShipmentFulfillmentForm, { ShipmentModalMode } from './ShipmentFulfillmentForm'
import CommonConfirmationModal from 'src/utils/modals/ConfirmationModal'

type ManageShipmentsModalProps = {
  show: boolean
  onClose: () => void
  orderId: number | null
  orderCode: string
  initialOpenId?: number | null
  forceViewMode?: boolean
  onSwitchRoute?: () => void
}

// Format "2026-03-25T11:24:00Z" or similar to a readable date without seconds
const formatDate = (dateStr: string) => {
  if (!dateStr) return 'N/A'
  try {
    const d = new Date(dateStr)
    if (isNaN(d.getTime())) return dateStr
    return d.toLocaleString(undefined, {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    })
  } catch {
    return dateStr
  }
}

// Icons
const DeliveryCheckIcon: FC<{ color?: string }> = ({ color = '#28a745' }) => (
  <svg width='16' height='16' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <path d='M20 7H4C3.45 7 3 7.45 3 8V19C3 19.55 3.45 20 4 20H20C20.55 20 21 19.55 21 19V8C21 7.45 20.55 7 20 7Z' stroke={color} strokeWidth='1.8' strokeLinejoin='round' />
    <path d='M12 7V4L16 4V7' stroke={color} strokeWidth='1.8' strokeLinejoin='round' />
    <path d='M8 4H16' stroke={color} strokeWidth='1.8' strokeLinecap='round' />
    <path d='M8 4V7' stroke={color} strokeWidth='1.8' strokeLinecap='round' />
    <path d='M9 13L11 15L15 11' stroke={color} strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
  </svg>
)

const EyeViewIcon: FC<{ color?: string }> = ({ color = '#6c757d' }) => (
  <svg width='16' height='16' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <path d='M1 12C1 12 5 4 12 4C19 4 23 12 23 12C23 12 19 20 12 20C5 20 1 12 1 12Z' stroke={color} strokeWidth='1.8' strokeLinejoin='round' />
    <circle cx='12' cy='12' r='3' stroke={color} strokeWidth='1.8' />
  </svg>
)

const ManageShipmentsModal: FC<ManageShipmentsModalProps> = ({ show, onClose, orderId, orderCode, initialOpenId, forceViewMode, onSwitchRoute }) => {
  const dispatch = useAppDispatch()
  const { errorToast, successToast } = useAppToast()

  const {
    shipmentList,
    shipmentListLoading,
    deleteLoading,
  } = useAppSelector((state) => state.omsShipping)

  const [initialLoading, setInitialLoading] = useState(true)
  const [orderLinesList, setOrderLinesList] = useState<any[]>([])

  // Inline forms state
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [expandedShipmentId, setExpandedShipmentId] = useState<number | null>(null)
  const [activeFulfillmentMode, setActiveFulfillmentMode] = useState<ShipmentModalMode>('edit')

  const [deleteModalState, setDeleteModalState] = useState<{ show: boolean, id: number | null }>({ show: false, id: null })
  const [shippingRoute, setShippingRoute] = useState<ShippingRoute>('open')

  // ── Synchronous state reset on modal open (prevents stale-frame flicker) ──
  const [prevShow, setPrevShow] = useState(false)
  if (show && !prevShow) {
    setPrevShow(true)
    setInitialLoading(true)
    setShowCreateForm(false)
    setExpandedShipmentId(null)
    setShippingRoute('open')
  }
  if (!show && prevShow) {
    setPrevShow(false)
  }

  const fetchOrderLines = useCallback(async () => {
    if (!orderId) return
    try {
      const olRes = await dispatch(getOrderLinesForPackageAsync({ orderId, page: 1, limit: 50, direct_shipment: true })).unwrap()
      const lines = olRes?.results?.data || olRes?.data || []
      setOrderLinesList(Array.isArray(lines) ? lines : [])
    } catch (err) {
      console.error('Failed to fetch order lines', err)
    }
  }, [dispatch, orderId])

  const fetchInitialData = async () => {
    if (!orderId) return
    setInitialLoading(true)
    try {
      // Determine shipping route using centralized gate function
      const route = await determineShippingRoute(orderId, dispatch)
      setShippingRoute(route)

      await dispatch(getOmsShipmentListAsync({ order_id: String(orderId), page: 1, limit: 50 })).unwrap()
      await fetchOrderLines()
    } catch (err: any) {
      errorToast(getApiErrorMsg(err, 'Failed to fetch shipment data'))
    } finally {
      setInitialLoading(false)
    }
  }

  useEffect(() => {
    if (show) {
      fetchInitialData()
    }
    // eslint-disable-line react-hooks/exhaustive-deps
  }, [show, orderId])

  useEffect(() => {
    if (show && initialOpenId && !initialLoading && shipmentList.length) {
      const shipment = shipmentList.find((s: any) => s.id === initialOpenId)
      if (shipment) {
        handleOpenInlineForm(forceViewMode ? 'view' : 'edit', shipment)
      }
    }
    // eslint-disable-line react-hooks/exhaustive-deps
  }, [show, initialOpenId, initialLoading, shipmentList, forceViewMode])

  const handleDeleteShipment = (shipmentId: number) => {
    setDeleteModalState({ show: true, id: shipmentId })
  }

  const proceedDeleteShipment = async () => {
    if (!deleteModalState.id) return
    try {
      await dispatch(deleteOmsShipmentAsync({ id: deleteModalState.id })).unwrap()
      successToast('Shipment deleted successfully.')
      dispatch(getOmsShipmentListAsync({ order_id: String(orderId), page: 1, limit: 50 }))
      fetchOrderLines()
    } catch (err: any) {
      errorToast(getApiErrorMsg(err, 'Failed to delete shipment.'))
    } finally {
      setDeleteModalState({ show: false, id: null })
    }
  }

  const handleOpenInlineForm = (mode: ShipmentModalMode, shipment: any) => {
    // Hide create form if it was open
    setShowCreateForm(false)
    setActiveFulfillmentMode(mode)
    setExpandedShipmentId(shipment.id)
  }

  const handleFulfillmentSuccess = () => {
    dispatch(getOmsShipmentListAsync({ order_id: String(orderId), page: 1, limit: 50 }))
    fetchOrderLines()
  }

  const routeBlocked = shippingRoute === 'packages'
  const noPendingQty = !initialLoading && !hasPendingQty(orderLinesList)
  const isCreateBtnDisabled = (routeBlocked || noPendingQty) && !showCreateForm

  return (
    <>
      <style>{`
        .modal-half-width {
          max-width: 50vw !important;
          width: 50vw !important;
        }
        .professional-modal input::placeholder,
        .professional-modal textarea::placeholder {
          color: #adb5bd !important;
          opacity: 0.6 !important;
        }
        .route-switch-link {
          color: #0d6efd;
          cursor: pointer;
          text-decoration: underline;
          transition: color 0.2s ease;
        }
        .route-switch-link:hover {
          color: #0a58ca;
        }
      `}</style>
      <Modal show={show} onHide={onClose} centered dialogClassName='professional-modal modal-half-width' backdrop='static' backdropClassName='blur-backdrop'>
        <Modal.Header closeButton>
          <div>
            <Modal.Title style={{ fontSize: '18px', fontWeight: 700 }}>
              🚚 Manage Shipments
            </Modal.Title>
            <Badge bg='light' text='secondary' className='border border-secondary px-3 py-2 rounded-pill' style={{ fontSize: '12px', fontWeight: 500 }}>
              Order {orderCode || `#${orderId}`}
            </Badge>
          </div>
        </Modal.Header>

        <Modal.Body>
          {initialLoading ? (
            <div className='d-flex justify-content-center align-items-center py-5'>
              <Spinner animation='border' size='sm' className='me-2' />
              <span className='text-muted'>Loading shipment data...</span>
            </div>
          ) : (
            <>
              {shippingRoute === 'packages' && (
                <Alert variant="info" className="mb-4" style={{ fontSize: '13px', border: '1px solid #b6d4fe' }}>
                  ℹ️ <strong>Note:</strong> Packages already exist for this order. Please use the <strong className="route-switch-link" onClick={onSwitchRoute}>'Manage Packages'</strong> menu to ship them.
                </Alert>
              )}
              <div className='mb-4'>
                <div className='d-flex justify-content-between align-items-center mb-4'>
                  <h6 className='fw-bold mb-0' style={{ fontSize: '16px', color: '#1e293b' }}>Existing Shipments</h6>
                  <OverlayTrigger
                    placement="left"
                    overlay={
                      isCreateBtnDisabled ? (
                        <Tooltip id="has-packages-tooltip">
                          {routeBlocked
                            ? "Packages already exist for this order. Please use the 'Manage Packages' menu to ship them."
                            : "All items have been fulfilled."}
                        </Tooltip>
                      ) : <Tooltip id="add-shipment-tooltip">Create a new direct shipment</Tooltip>
                    }
                  >
                    <span className="d-inline-block">
                      <Button
                        variant={showCreateForm ? 'light' : 'primary'}
                        size='sm'
                        disabled={isCreateBtnDisabled}
                        style={{
                          fontSize: '13px',
                          padding: '8px 20px',
                          fontWeight: 600,
                          borderRadius: '6px',
                          transition: 'all 0.2s ease-in-out',
                          backgroundColor: showCreateForm ? '#f1f5f9' : (isCreateBtnDisabled ? '#e2e8f0' : '#0d6efd'),
                          color: showCreateForm ? '#475569' : (isCreateBtnDisabled ? '#94a3b8' : '#fff'),
                          border: showCreateForm ? '1px solid #e2e8f0' : 'none',
                          boxShadow: showCreateForm || isCreateBtnDisabled ? 'none' : '0 4px 6px -1px rgba(13, 110, 253, 0.2)',
                          pointerEvents: isCreateBtnDisabled ? 'none' : 'auto'
                        }}
                        onClick={() => {
                          setExpandedShipmentId(null)
                          setShowCreateForm(!showCreateForm)
                        }}
                      >
                        {showCreateForm ? 'Cancel New Shipment' : '+ Add New Shipment'}
                      </Button>
                    </span>
                  </OverlayTrigger>
                </div>

                {/* Inline Create Form */}
                {showCreateForm && (
                  <div className="mb-4 shadow-sm" style={{ transition: 'all 0.3s ease' }}>
                    <ShipmentFulfillmentForm
                      onCancel={() => setShowCreateForm(false)}
                      orderId={orderId}
                      orderCode={orderCode}
                      pkg={null}
                      shipmentData={null}
                      orderLinesList={orderLinesList}
                      onSuccess={() => {
                        setShowCreateForm(false)
                        handleFulfillmentSuccess()
                      }}
                      isDirect={true}
                      mode="create"
                    />
                  </div>
                )}

                {/* Only show shipment list if Create form is NOT open and no packages exist */}
                {!showCreateForm && shippingRoute !== 'packages' && (
                  <>
                    {shipmentListLoading ? (
                      <div className='d-flex justify-content-center py-5'>
                        <Spinner animation='border' className='text-primary' />
                      </div>
                    ) : shipmentList.length === 0 ? (
                      <div className='text-center py-5'>
                        <p className='text-muted mb-0' style={{ fontSize: '14px' }}>No shipments created yet for this order.</p>
                      </div>
                    ) : (
                      <div className='d-flex flex-column gap-3'>
                        {shipmentList.map((shipment: any, idx: number) => {
                          const status = shipment.shipment_status
                          const isDelivered = status === 'DELIVERED'
                          const isExpanded = expandedShipmentId === shipment.id

                          let badgeBg = 'secondary'
                          if (status === 'SHIPPED') badgeBg = 'primary'
                          if (status === 'DELIVERED') badgeBg = 'success'
                          if (status === 'PENDING') badgeBg = 'warning text-dark'

                          return (
                            <div
                              key={shipment.id}
                              className={`rounded border transition-all duration-200 ${isExpanded ? 'shadow-sm border-primary' : 'hover:shadow-md'}`}
                              style={{
                                overflow: 'hidden',
                                backgroundColor: '#fff',
                                borderColor: isExpanded ? '#0d6efd' : '#dee2e6',
                                transition: 'all 0.2s ease-in-out'
                              }}
                            >
                              {/* Card Header */}
                              <div
                                className='px-3 py-2 d-flex justify-content-between align-items-center'
                                style={{
                                  backgroundColor: isExpanded ? '#f0f7ff' : '#f8f9fa',
                                  borderBottom: isExpanded ? 'none' : '1px solid #dee2e6',
                                  transition: 'background-color 0.2s ease-in-out'
                                }}
                              >
                                <div className='d-flex align-items-center gap-2'>
                                  <strong style={{ fontSize: '14px', color: isExpanded ? '#0d6efd' : '#333' }}>
                                    🚚 Shipment {idx + 1}
                                  </strong>
                                  {shipment.shipment_number && (
                                    <span className='text-muted ms-1' style={{ fontSize: '13px' }}>
                                      ({shipment.shipment_number})
                                    </span>
                                  )}
                                  <Badge bg={badgeBg} className='ms-2' style={{ fontSize: '11px' }}>
                                    {status || 'UNKNOWN'}
                                  </Badge>
                                </div>
                                <div className='d-flex align-items-center gap-2'>
                                  {!isDelivered && (
                                    <>
                                      <OverlayTrigger placement="top" overlay={<Tooltip id={`edit-tooltip-${shipment.id}`}>Edit tracking and carrier details</Tooltip>}>
                                        <Button
                                          variant={isExpanded && activeFulfillmentMode === 'edit' ? 'primary' : 'light'}
                                          size='sm'
                                          style={{
                                            fontSize: '12px',
                                            padding: '4px 12px',
                                            backgroundColor: isExpanded && activeFulfillmentMode === 'edit' ? '#0d6efd' : '#fff',
                                            border: '1px solid',
                                            borderColor: isExpanded && activeFulfillmentMode === 'edit' ? '#0d6efd' : '#dee2e6',
                                            color: isExpanded && activeFulfillmentMode === 'edit' ? '#fff' : '#0d6efd',
                                          }}
                                          onClick={() => handleOpenInlineForm('edit', shipment)}
                                        >
                                          Edit
                                        </Button>
                                      </OverlayTrigger>
                                      {status !== 'SHIPPED' && (
                                        <OverlayTrigger placement="top" overlay={<Tooltip id={`delete-tooltip-${shipment.id}`}>Remove this shipment</Tooltip>}>
                                          <Button
                                            variant='light'
                                            size='sm'
                                            style={{
                                              fontSize: '12px',
                                              padding: '4px 12px',
                                              backgroundColor: '#fff',
                                              border: '1px solid #dee2e6',
                                              color: 'red',
                                            }}
                                            disabled={deleteLoading}
                                            onClick={() => handleDeleteShipment(shipment.id)}
                                          >
                                            Delete
                                          </Button>
                                        </OverlayTrigger>
                                      )}
                                    </>
                                  )}
                                  <OverlayTrigger placement="top" overlay={<Tooltip id={`view-deliver-tooltip-${shipment.id}`}>{isDelivered ? 'View shipment details' : 'Mark shipment as delivered'}</Tooltip>}>
                                    <Button
                                      variant={isExpanded && (activeFulfillmentMode === 'deliver' || activeFulfillmentMode === 'view') ? 'success' : 'light'}
                                      size='sm'
                                      style={{
                                        fontSize: '13px',
                                        padding: '4px 8px',
                                        backgroundColor: isExpanded && (activeFulfillmentMode === 'deliver' || activeFulfillmentMode === 'view') ? '#198754' : '#fff',
                                        border: '1px solid',
                                        borderColor: isExpanded && (activeFulfillmentMode === 'deliver' || activeFulfillmentMode === 'view') ? '#198754' : '#dee2e6',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        minWidth: '32px',
                                      }}
                                      onClick={() => handleOpenInlineForm(isDelivered ? 'view' : 'deliver', shipment)}
                                    >
                                      {isDelivered ? (
                                        <EyeViewIcon color={isExpanded && activeFulfillmentMode === 'view' ? '#fff' : '#6c757d'} />
                                      ) : (
                                        <DeliveryCheckIcon color={isExpanded && activeFulfillmentMode === 'deliver' ? '#fff' : '#28a745'} />
                                      )}
                                    </Button>
                                  </OverlayTrigger>
                                </div>
                              </div>

                              {/* Card Body - Expands conditionally into Form or Read-Only View */}
                              <div style={{ transition: 'all 0.3s ease-in-out' }}>
                                {isExpanded ? (
                                  <div style={{ borderTop: '1px solid #dee2e6' }}>
                                    <ShipmentFulfillmentForm
                                      onCancel={() => setExpandedShipmentId(null)}
                                      orderId={orderId}
                                      orderCode={orderCode}
                                      pkg={null}
                                      shipmentData={shipment}
                                      shipmentIndex={idx}
                                      orderLinesList={orderLinesList}
                                      onSuccess={() => {
                                        setExpandedShipmentId(null)
                                        handleFulfillmentSuccess()
                                      }}
                                      isDirect={true}
                                      mode={activeFulfillmentMode}
                                    />
                                  </div>
                                ) : (
                                  <div
                                    className='p-4'
                                    style={{
                                      display: 'flex',
                                      gap: '24px',
                                      alignItems: 'stretch'
                                    }}
                                  >
                                    {/* Left column: Shipment Info */}
                                    <div style={{ flex: '1', fontSize: '13px', color: '#555' }}>
                                      <div className='d-flex mb-2 align-items-center'>
                                        <span className='text-muted' style={{ width: '100px', fontWeight: 500 }}>Carrier</span>
                                        <span className='fw-semibold text-dark'>{shipment.shipping_method || 'N/A'}</span>
                                      </div>
                                      <div className='d-flex mb-2 align-items-center'>
                                        <span className='text-muted' style={{ width: '100px', fontWeight: 500 }}>Ship Date</span>
                                        <span className='fw-semibold text-dark'>{formatDate(shipment.api_created_at || shipment.updated_at || shipment.ship_date)}</span>
                                      </div>
                                      <div className='d-flex mb-2 align-items-center'>
                                        <span className='text-muted' style={{ width: '100px', fontWeight: 500 }}>Tracking</span>
                                        <span className='fw-semibold text-primary'>
                                          {shipment.tracking_url && shipment.tracking_number ? (
                                            <a href={shipment.tracking_url} target='_blank' rel='noreferrer' style={{ textDecoration: 'none' }}>
                                              {shipment.tracking_number}
                                            </a>
                                          ) : (
                                            <span className='text-dark'>{shipment.tracking_number || 'N/A'}</span>
                                          )}
                                        </span>
                                      </div>
                                      {isDelivered && shipment.delivery_date && (
                                        <div className='d-flex mb-2 align-items-center'>
                                          <span className='text-muted' style={{ width: '100px', fontWeight: 500 }}>Delivery Date</span>
                                          <span className='text-success fw-bold'>{formatDate(shipment.delivery_date)}</span>
                                        </div>
                                      )}
                                    </div>

                                    {/* Vertical Divider */}
                                    <div style={{ width: '1px', backgroundColor: '#eee', margin: '0 8px' }}></div>

                                    {/* Right column: Line Items */}
                                    <div style={{ flex: '1.2' }}>
                                      <span className='fw-bold text-dark d-block mb-2' style={{ fontSize: '13px' }}>
                                        Items in shipment
                                      </span>
                                      <div
                                        className='rounded px-3 py-2'
                                        style={{ backgroundColor: '#f9f9f9', border: '1px solid #f0f0f0', maxHeight: '120px', overflowY: 'auto' }}
                                      >
                                        {Array.isArray(shipment.line_items) && shipment.line_items.length > 0 ? (
                                          shipment.line_items.map((li: any, idx: number) => {
                                            const itemId = li.platform_order_line_item ?? li.order_item_id ?? li.id
                                            const matchedLine = orderLinesList.find((ol) => ol.id === itemId)
                                            const itemName = li.name ?? matchedLine?.name ?? matchedLine?.platform_product?.name ?? `Item #${itemId}`
                                            const qty = li.quantity ?? li.package_quantity ?? 0
                                            return (
                                              <div key={idx} className='d-flex align-items-start py-1 mb-1 border-bottom border-light' style={{ fontSize: '13px', color: '#555' }}>
                                                <span className='fw-bold text-dark me-2' style={{ minWidth: '24px' }}>{qty}x</span>
                                                <span>{itemName}</span>
                                              </div>
                                            )
                                          })
                                        ) : (
                                          <span style={{ fontSize: '13px', color: '#888' }}>No items</span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </>
                )}
                {/* End conditional shipment list render */}
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant='light'
            onClick={onClose}
            style={{ padding: '10px 28px', fontWeight: 600, color: '#475569', border: '1px solid #cbd5e1', borderRadius: '8px' }}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <CommonConfirmationModal
        show={deleteModalState.show}
        title="Delete Shipment"
        message="Are you sure you want to delete this shipment?"
        confirmText="Delete"
        confirmVariant="danger"
        onConfirm={proceedDeleteShipment}
        onCancel={() => setDeleteModalState({ show: false, id: null })}
      />
    </>
  )
}

export default ManageShipmentsModal
